import React, { useState, useEffect } from 'react';
import { Spin, Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ChatbotWidget from '../../../components/embed/ChatbotWidget';
import EmbedLayout from '../../../components/embed/EmbedLayout';
import { parseEmbedParams } from '../../../utils/embedUtils';
import { validateEmbedConfig, mergeEmbedConfig } from '../../../configs/EmbedConfig';

// Utility to check if running in an iframe
const isInIframe = () => window.self !== window.top;

const ChatAgentIframe = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({});
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    if (isInIframe()) {
      // Hide header and footer if present
      const header = document.querySelector('header');
      const footer = document.querySelector('footer');
      if (header) header.style.display = 'none';
      if (footer) footer.style.display = 'none';
    }
    
    // Listen for widget maximize messages
    const handleWidgetMessages = (event) => {
      if (event.data.type === 'vlm-chatbot-widget-maximized' && event.data.data.newSession) {
        console.log('🔄 Widget maximized with new session:', event.data.data);
        
        // Update config with new chat ID
        if (event.data.data.newChatId) {
          setConfig(prevConfig => ({
            ...prevConfig,
            chatId: event.data.data.newChatId,
            conditions: {
              ...prevConfig.conditions,
              maximizeForChat: true
            }
          }));
          console.log('✨ Updated config with new chat ID:', event.data.data.newChatId);
        }
      }
    };
    
    window.addEventListener('message', handleWidgetMessages);
    
    initializeChat();
    
    return () => {
      window.removeEventListener('message', handleWidgetMessages);
    };
  }, []);

  const initializeChat = () => {
    try {
      // Parse configuration from URL parameters
      const urlParams = parseEmbedParams();

      // Validate configuration
      const validation = validateEmbedConfig(urlParams);

      if (!validation.isValid) {
        throw new Error(`Configuration error: ${validation.errors.join(', ')}`);
      }

      // Merge with default configuration
      const finalConfig = mergeEmbedConfig(urlParams);
      setConfig(finalConfig);
      setLoading(false);

    } catch (error) {
      console.error('Chat initialization failed:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    initializeChat();
  };

  const handleWidgetError = (error) => {
    // Only set error if it's a fatal error (e.g., config/initialization)
    // For API/chat errors, do nothing (handled inline in the widget)
    if (error?.isFatal) {
      setError(error.message || 'Chat widget encountered an error');
    } else {
      // Log for debugging, but do not block the UI
      console.error('Chat widget error:', error);
    }
  };

  const handleWidgetClose = () => {
    // In iframe mode, we can't close the window, just show a message
    console.log('Widget close requested');
  };

  const handleWidgetMinimize = () => {
    // In iframe mode, track minimize state to adjust layout
    setIsMinimized(true);
    console.log('Widget minimize requested');
  };

  const handleWidgetMaximize = () => {
    // Handle widget maximize with potential UUID regeneration
    setIsMinimized(false);
    console.log('Widget maximize requested - triggering new session conditions');
    
    // Update URL to include maximize condition for next reload/refresh
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('maximizeForChat', 'true');
      
      // If askConfirmation is not already set, add it
      if (!currentUrl.searchParams.has('askConfirmation')) {
        currentUrl.searchParams.set('askConfirmation', 'true');
      }
      
      // Update browser URL without reload
      window.history.replaceState({}, '', currentUrl.toString());
      console.log('🔄 Updated URL with maximize conditions:', currentUrl.toString());
    }
  };

  const handleMessageSent = (message) => {
    console.log('Message sent:', message);
  };

  const handleMessageReceived = (message) => {
    console.log('Message received:', message);
  };

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        padding: '20px',
        textAlign: 'center'
      }}>
        <Alert
          message="Chat Unavailable"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px', maxWidth: '400px' }}
        />
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRetry}
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#fafafa'
      }}>
        <Spin size="large" />
        <span style={{ marginLeft: '12px' }}>Loading chat...</span>
      </div>
    );
  }

  if (!config || Object.keys(config).length === 0) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#fafafa'
      }}>
        <div>Loading chat...</div>
      </div>
    );
  }

  // Only render the chat widget (no header/footer) in iframe mode
  return (
    <EmbedLayout config={config} isMinimized={isMinimized}>
      <ChatbotWidget
        config={config}
        onError={handleWidgetError}
        onClose={handleWidgetClose}
        onMinimize={handleWidgetMinimize}
        onMaximize={handleWidgetMaximize}
        onMessageSent={handleMessageSent}
        onMessageReceived={handleMessageReceived}
        widgetError={null}
      />
    </EmbedLayout>
  );
};

export default ChatAgentIframe;